import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  connectEtelecomZbsOa,
  getEtelecomZbsOaStatus,
  listEtelecomZbsTemplates,
  sendEtelecomZbsZns,
} from '../../api/etelecomZbs'
import { useOAuthPopup } from '../../hooks/useOAuthPopup'
import { formatApiError } from '../../lib/apiErrorMessage'

// Styles mirror the channel cards in ChatIntegrations.jsx (cardStyle,
// labelStyle, inputStyle, btnPrimary, and the connected/not-connected
// badges rendered in the CHANNELS.map header) so this card reads as "one
// more integration card in the list" without importing from that file.
const cardStyle = { background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 12, padding: 18, marginBottom: 12 }
const inputStyle = { width: '100%', padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: '#f5f6f8', color: '#374151', cursor: 'default', outline: 'none', boxSizing: 'border-box' }
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block', marginTop: 8 }
const btnPrimary = { background: '#111', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 14 }
const badgeConnected = { fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: 20 }
const badgeNotConnected = { fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 20 }
const sectionDivider = { borderTop: '0.5px solid #e0e3f0', marginTop: 16, paddingTop: 14 }
const textareaStyle = { ...inputStyle, cursor: 'text', minHeight: 60, fontFamily: 'monospace', resize: 'vertical' }
const errorTextStyle = { fontSize: 12, color: '#ef4444', marginTop: 8 }
const badgeEnable = { fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: 20, flexShrink: 0 }
const badgeDisable = { fontSize: 11, fontWeight: 600, color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 20, flexShrink: 0 }
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }
const modalCard = { background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 900, maxHeight: '88vh', overflowY: 'auto' }
const closeCircleBtn = { width: 28, height: 28, borderRadius: '50%', border: '0.5px solid #e0e3f0', background: '#fff', color: '#888', fontSize: 14, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }
const detailGrid = { display: 'flex', gap: 24, marginTop: 8, flexWrap: 'wrap-reverse' }
const detailLeftCol = { flex: '1 1 320px', minWidth: 280 }
const detailRightCol = { flex: '1 1 280px', minWidth: 240, background: '#f5f6f8', borderRadius: 12, padding: 16 }
const kvRow = { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderBottom: '0.5px solid #f0f1f5', fontSize: 13 }
const kvRowLabel = { color: '#555', flexShrink: 0 }
const kvRowValue = { color: '#111', fontWeight: 600, textAlign: 'right' }
const paramsTable = { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 6 }
const paramsTh = { textAlign: 'left', padding: '6px 8px', background: '#f5f6f8', color: '#555', fontWeight: 600, border: '0.5px solid #e0e3f0' }
const paramsTd = { padding: '6px 8px', border: '0.5px solid #e0e3f0', color: '#111' }
const templateGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginTop: 8 }
const templateCard = { textAlign: 'left', border: '0.5px solid #e0e3f0', borderRadius: 8, padding: 10, background: '#fff', cursor: 'pointer' }

function TemplateStatusBadge({ value }) {
  const key = String(value || '').toUpperCase()
  return <span style={key === 'ENABLE' ? badgeEnable : badgeDisable}>{value || '—'}</span>
}

function formatThousands(n) {
  const num = Number(n)
  // A message quota count, not money: digit grouping only, no currency.
  // eslint-disable-next-line no-restricted-syntax
  return Number.isFinite(num) ? num.toLocaleString('vi-VN') : String(n)
}

function formatDateTime(v) {
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  const pad = (x) => String(x).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
}

// eTelecom ListTemplates/CreateTemplate/GetTemplate all return this same
// field set (see eTelecom integration PDF §18-20) -- ordered + labeled to
// match the target ZNS-template-detail UI. Only fields actually present on
// `row` render; unknown extra fields eTelecom might add still surface via
// the generic fallback list below so nothing is silently hidden.
function useTemplateFieldDefs(t) {
  const statusLabel = (v) => ({
    ENABLE: t('chatIntegrations.zbsTemplateStatusEnable', { defaultValue: 'Đã duyệt' }),
    DISABLE: t('chatIntegrations.zbsTemplateStatusDisable', { defaultValue: 'Bị khóa' }),
    PENDING_REVIEW: t('chatIntegrations.zbsTemplateStatusPending', { defaultValue: 'Đang duyệt' }),
    REJECT: t('chatIntegrations.zbsTemplateStatusReject', { defaultValue: 'Bị từ chối' }),
  }[String(v).toUpperCase()] || t('chatIntegrations.zbsTemplateStatusUnknown', { defaultValue: 'Không xác định' }))

  const typeLabel = (v) => ({
    text: t('chatIntegrations.zbsTemplateTypeText', { defaultValue: 'Văn bản' }),
    otp: t('chatIntegrations.zbsTemplateTypeOtp', { defaultValue: 'OTP' }),
    table: t('chatIntegrations.zbsTemplateTypeTable', { defaultValue: 'Bảng' }),
    rating: t('chatIntegrations.zbsTemplateTypeRating', { defaultValue: 'Đánh giá' }),
  }[String(v).toLowerCase()] || t('chatIntegrations.zbsTemplateTypeUnknown', { defaultValue: 'Không xác định' }))

  const qualityLabel = (v) => ({
    LOW: t('chatIntegrations.zbsTemplateQualityLow', { defaultValue: 'Kém' }),
    MEDIUM: t('chatIntegrations.zbsTemplateQualityMedium', { defaultValue: 'Trung bình' }),
    HIGH: t('chatIntegrations.zbsTemplateQualityHigh', { defaultValue: 'Cao' }),
  }[String(v).toUpperCase()] || t('chatIntegrations.zbsTemplateQualityUndefined', { defaultValue: 'Chưa được xác định' }))

  const tagLabel = (v) => ({
    OTP: t('chatIntegrations.zbsTemplateTagOtp', { defaultValue: 'Tin OTP' }),
    IN_TRANSACTION: t('chatIntegrations.zbsTemplateTagInTransaction', { defaultValue: 'Xác nhận/Cập nhật giao dịch' }),
    POST_TRANSACTION: t('chatIntegrations.zbsTemplateTagPostTransaction', { defaultValue: 'Hỗ trợ sau giao dịch' }),
    ACCOUNT_UPDATE: t('chatIntegrations.zbsTemplateTagAccountUpdate', { defaultValue: 'Cập nhật tài khoản' }),
    GENERAL_UPDATE: t('chatIntegrations.zbsTemplateTagGeneralUpdate', { defaultValue: 'Thay đổi thông tin dịch vụ' }),
    FOLLOW_UP: t('chatIntegrations.zbsTemplateTagFollowUp', { defaultValue: 'Thông báo ưu đãi' }),
  }[String(v).toUpperCase()] || t('chatIntegrations.zbsTemplateTagUnknown', { defaultValue: 'Không xác định' }))

  const boolLabel = (v) => (v
    ? t('chatIntegrations.zbsYes', { defaultValue: 'Có' })
    : t('chatIntegrations.zbsNo', { defaultValue: 'Không' }))

  return [
    ['template_status', t('chatIntegrations.zbsTemplateFieldStatus', { defaultValue: 'Trạng thái' }), statusLabel],
    ['template_id', t('chatIntegrations.zbsTemplateFieldId', { defaultValue: 'ID mẫu ZNS' }), (v) => v],
    ['type', t('chatIntegrations.zbsTemplateFieldType', { defaultValue: 'Loại mẫu' }), typeLabel],
    ['template_quality', t('chatIntegrations.zbsTemplateFieldQuality', { defaultValue: 'Chất lượng mẫu tin' }), qualityLabel],
    ['template_tag', t('chatIntegrations.zbsTemplateFieldTag', { defaultValue: 'Cấp độ mẫu tin' }), tagLabel],
    ['price', t('chatIntegrations.zbsTemplateFieldPrice', { defaultValue: 'Đơn giá' }), (v) => `${formatThousands(v)}đ/ZNS`],
    ['timeout', t('chatIntegrations.zbsTemplateFieldZTime', { defaultValue: 'ZTime' }), (v) => `${formatThousands(v)} ${t('chatIntegrations.zbsTemplateFieldSeconds', { defaultValue: 'giây' })}`],
    ['template_daily_quota', t('chatIntegrations.zbsTemplateFieldDailyQuota', { defaultValue: 'Giới hạn gửi/ngày' }), formatThousands],
    ['template_remaining_quota', t('chatIntegrations.zbsTemplateFieldRemainingQuota', { defaultValue: 'Số lượng còn lại/ngày' }), formatThousands],
    ['apply_template_quota', t('chatIntegrations.zbsTemplateFieldApplyQuota', { defaultValue: 'Áp dụng giới hạn mẫu tin' }), boolLabel],
    ['created_at', t('chatIntegrations.zbsTemplateFieldCreatedAt', { defaultValue: 'Thời gian tạo' }), formatDateTime],
    ['updated_at', t('chatIntegrations.zbsTemplateFieldUpdatedAt', { defaultValue: 'Thời gian cập nhật' }), formatDateTime],
  ]
}

// template_params' own shape isn't documented beyond "array object" (see
// eTelecom PDF §18-20), so this tries the common Zalo ZNS param field names
// first and only falls back to raw per-object columns if none match --
// never silently drops data just because a key name guess is off.
function TemplateParamsTable({ params, t }) {
  if (!Array.isArray(params) || params.length === 0 || !params.every((p) => p && typeof p === 'object')) {
    return <span style={{ color: '#888', fontSize: 13 }}>—</span>
  }
  const NAME_KEYS = ['name', 'param_name', 'key']
  const LEN_KEYS = ['maxLength', 'max_length', 'length']
  const TYPE_KEYS = ['type', 'data_type', 'dataType']
  const pick = (obj, keys) => keys.map((k) => obj[k]).find((v) => v !== undefined)
  const matched = params.some((p) => [...NAME_KEYS, ...LEN_KEYS, ...TYPE_KEYS].some((k) => p[k] !== undefined))

  if (matched) {
    return (
      <table style={paramsTable}>
        <thead>
          <tr>
            <th style={paramsTh}>{t('chatIntegrations.zbsTemplateParamName', { defaultValue: 'Tên tham số' })}</th>
            <th style={paramsTh}>{t('chatIntegrations.zbsTemplateParamLength', { defaultValue: 'Chiều dài kí tự' })}</th>
            <th style={paramsTh}>{t('chatIntegrations.zbsTemplateParamType', { defaultValue: 'Loại dữ liệu' })}</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p, i) => (
            <tr key={i}>
              <td style={paramsTd}>{pick(p, NAME_KEYS) ?? '—'}</td>
              <td style={paramsTd}>{pick(p, LEN_KEYS) ?? '—'}</td>
              <td style={paramsTd}>{pick(p, TYPE_KEYS) ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  const cols = [...new Set(params.flatMap((p) => Object.keys(p)))]
  return (
    <table style={paramsTable}>
      <thead>
        <tr>{cols.map((c) => <th key={c} style={paramsTh}>{c}</th>)}</tr>
      </thead>
      <tbody>
        {params.map((p, i) => (
          <tr key={i}>
            {cols.map((c) => (
              <td key={c} style={paramsTd}>{p[c] === undefined || p[c] === null ? '—' : String(p[c])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// Shows every field the eTelecom template row has (id/branch_id/env/status
// plus whatever eTelecom's own CreateTemplate/ListTemplates response
// contains -- see _template_response in
// app/routers/admin_etelecom_zbs_templates.py). Known eTelecom fields get
// nice Vietnamese labels + a dedicated params table; anything else still
// shows up under a generic fallback list so nothing is silently hidden.
function TemplateDetailModal({ row, onClose, t }) {
  const fieldDefs = useTemplateFieldDefs(t)
  if (!row) return null

  const knownKeys = new Set(fieldDefs.map(([key]) => key))
  const shownRows = fieldDefs.filter(([key]) => row[key] !== undefined && row[key] !== null)
  const extraEntries = Object.entries(row).filter(
    ([k, v]) => k !== 'preview_url' && k !== 'template_params' && !knownKeys.has(k) && v !== null && v !== undefined,
  )

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <p style={{ fontSize: 18, fontWeight: 700, color: '#111' }}>
            {t('chatIntegrations.zbsTemplateDetailTitle', { defaultValue: 'Chi tiết mẫu ZNS' })}
          </p>
          <button type="button" style={closeCircleBtn} onClick={onClose} aria-label={t('chatIntegrations.zbsTemplateClose', { defaultValue: 'Đóng' })}>
            ✕
          </button>
        </div>

        <div style={detailGrid}>
          <div style={detailLeftCol}>
            {shownRows.map(([key, label, format]) => (
              <div key={key} style={kvRow}>
                <span style={kvRowLabel}>{label}</span>
                <span style={{ ...kvRowValue, color: key === 'template_status' && String(row[key]).toUpperCase() === 'ENABLE' ? '#16a34a' : kvRowValue.color }}>
                  {format(row[key])}
                </span>
              </div>
            ))}

            <div style={{ ...kvRow, alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
              <span style={kvRowLabel}>{t('chatIntegrations.zbsTemplateFieldParams', { defaultValue: 'Tham số' })}</span>
              <TemplateParamsTable params={row.template_params} t={t} />
            </div>

            {extraEntries.map(([key, value]) => (
              <div key={key} style={kvRow}>
                <span style={kvRowLabel}>{key}</span>
                <span style={kvRowValue}>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
              </div>
            ))}
          </div>

          {row.preview_url && (
            <div style={detailRightCol}>
              <iframe
                src={row.preview_url}
                title={row.template_name || row.template_id}
                style={{ width: '100%', height: 420, borderRadius: 8, border: 'none', background: '#fff' }}
                sandbox="allow-scripts"
              />
              <a
                href={row.preview_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', marginTop: 8, fontSize: 12, fontWeight: 600, color: '#111' }}
              >
                {t('chatIntegrations.zbsTemplateOpenPreview', { defaultValue: 'Mở xem trước' })} →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Read-only template list -- growchat-manager mirrors the admin app's
// template management (ai-receptionist-bot/frontend's ZaloZbsSection
// TemplateSection) but tenants can only view, not create/sync (see
// api/etelecomZbs.js's listEtelecomZbsTemplates).
function TemplateSection({ tenantId, botId, t }) {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function fetchTemplates() {
      setLoading(true)
      try {
        const data = await listEtelecomZbsTemplates(tenantId, botId)
        if (!cancelled) setTemplates(Array.isArray(data?.items) ? data.items : [])
      } catch {
        if (!cancelled) setTemplates([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (tenantId && botId) fetchTemplates()
    return () => { cancelled = true }
  }, [tenantId, botId])

  return (
    <div style={sectionDivider}>
      <p style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 8 }}>
        {t('chatIntegrations.zbsTemplateTitle', { defaultValue: 'Mẫu ZNS' })}
      </p>
      {loading ? (
        <p style={{ fontSize: 12, color: '#888' }}>{t('chatIntegrations.zbsTemplateLoading', { defaultValue: 'Đang tải...' })}</p>
      ) : templates.length === 0 ? (
        <p style={{ fontSize: 12, color: '#888' }}>{t('chatIntegrations.zbsTemplateEmpty', { defaultValue: 'Chưa có mẫu nào.' })}</p>
      ) : (
        <div style={templateGrid}>
          {templates.map((row) => (
            <button
              key={row.id || row.template_id}
              type="button"
              style={templateCard}
              onClick={() => setSelected(row)}
            >
              <p style={{ fontSize: 12, fontWeight: 600, color: '#111', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.template_name || row.template_id}
              </p>
              <p style={{ fontSize: 11, color: '#888', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.template_id}
              </p>
              <TemplateStatusBadge value={row.template_status || row.status} />
            </button>
          ))}
        </div>
      )}

      {selected && <TemplateDetailModal row={selected} onClose={() => setSelected(null)} t={t} />}
    </div>
  )
}

/**
 * Send ZNS + message status (Phase 4). Only rendered once the OA is
 * connected. `bot_id` follows the same "tenantId doubles as bot_id" note as
 * ZaloZbsCard itself (this app has no separate multi-bot concept per tenant
 * session).
 */
function SendZnsSection({ tenantId, botId, t }) {
  const [phone, setPhone] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [templateDataText, setTemplateDataText] = useState('{}')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState('')
  const [sendResult, setSendResult] = useState(null)

  async function handleSend(e) {
    e.preventDefault()
    setSendError('')
    setSendResult(null)
    const templateIdNum = Number(templateId)
    if (!Number.isInteger(templateIdNum)) {
      setSendError(t('chatIntegrations.zbsSendInvalidTemplateId', { defaultValue: 'Template ID phải là số nguyên' }))
      return
    }
    let templateData
    try {
      templateData = templateDataText.trim() ? JSON.parse(templateDataText) : {}
    } catch {
      setSendError(t('chatIntegrations.zbsSendInvalidJson', { defaultValue: 'template_data phải là JSON hợp lệ' }))
      return
    }
    setSending(true)
    try {
      const data = await sendEtelecomZbsZns(tenantId, { botId, phone, templateId: templateIdNum, templateData })
      setSendResult(data)
    } catch (err) {
      setSendError(formatApiError(err, t('chatIntegrations.zbsSendError', { defaultValue: 'Gửi ZNS thất bại' })))
    } finally {
      setSending(false)
    }
  }

  return (
    <div style={sectionDivider}>
      <p style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 8 }}>
        {t('chatIntegrations.zbsSendTitle', { defaultValue: 'Gửi ZNS' })}
      </p>

      <form onSubmit={handleSend}>
        <label style={labelStyle}>{t('chatIntegrations.zbsSendPhone', { defaultValue: 'Số điện thoại' })}</label>
        <input
          style={{ ...inputStyle, cursor: 'text' }}
          type="text"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
        />
        <label style={labelStyle}>{t('chatIntegrations.zbsSendTemplateId', { defaultValue: 'Template ID' })}</label>
        <input
          style={{ ...inputStyle, cursor: 'text' }}
          type="number"
          inputMode="numeric"
          step="1"
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          required
        />
        <label style={labelStyle}>{t('chatIntegrations.zbsSendTemplateData', { defaultValue: 'Template data (JSON)' })}</label>
        <textarea
          style={textareaStyle}
          value={templateDataText}
          onChange={(e) => setTemplateDataText(e.target.value)}
        />
        <button type="submit" style={btnPrimary} disabled={sending}>
          {sending
            ? t('chatIntegrations.zbsSending', { defaultValue: 'Đang gửi...' })
            : t('chatIntegrations.zbsSendSubmit', { defaultValue: 'Gửi' })}
        </button>
      </form>
      {sendError && <p style={errorTextStyle}>{sendError}</p>}
      {sendResult && (
        <pre style={{ ...inputStyle, marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {JSON.stringify(sendResult, null, 2)}
        </pre>
      )}
    </div>
  )
}

/**
 * "Zalo OA ZBS" integration card. Only shows up once the tenant already has
 * an eTelecom shop set up by an admin (Phase 1, separate app) — the
 * oa-status endpoint errors or has no shop context otherwise, and per spec
 * ("chỉ hiện khi có etelecom_AuthorizeOA_url") we fail closed: any non-2xx
 * response means "hide this section entirely", not an error banner.
 */
// `bot` is intentionally not accepted as a prop here: backend note
// (app/routers/manager_etelecom_zbs.py) documents that `bot_id` and
// `tenant_id` live in the same identifier space for this feature -- `bot_id`
// resolves via the exact same Mongo TenantDocument lookup as `tenant_id`.
// This app has no separate "bot id" of its own (elsewhere in
// ChatIntegrations.jsx, `getChannelBotSettings`/`updateChannelBotSetting`
// etc. are all keyed by `tenantId` directly, and the `bot` object passed
// around this file is just the tenant record from getTenant(tid) -- it has
// no `.id` field). So `tenantId` is sent as `bot_id`.
export default function ZaloZbsCard({ tenantId, zbsEnabled }) {
  const { t } = useTranslation()
  const botId = tenantId

  const [checked, setChecked] = useState(false)
  const [available, setAvailable] = useState(false)
  const [status, setStatus] = useState(null) // 'connected' | 'not_connected' | null
  const [oaInfo, setOaInfo] = useState(null)
  const [open, setOpen] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState('')
  const [authUrl, setAuthUrl] = useState('')
  const [pendingOpen, setPendingOpen] = useState(false)

  // Guards against a real race: `bot` (and therefore `zbsEnabled`) loads
  // asynchronously in the parent, so the FIRST loadStatus() call after mount
  // often fires with `zbsEnabled` still `undefined` (treated as enabled) and
  // kicks off a real oa-status network request. If `bot` then arrives with
  // `zbs_enabled: false`, a second loadStatus() call correctly hides the card
  // synchronously -- but the first call's in-flight promise can resolve LATER
  // and flip `available` back to true, overriding the correct hidden state.
  // Each call claims the latest requestId; only the most recent one is allowed
  // to apply its result.
  const requestIdRef = useRef(0)

  const loadStatus = useCallback(async () => {
    const myRequestId = ++requestIdRef.current
    const isStale = () => myRequestId !== requestIdRef.current

    // zbs_enabled === false is an explicit admin opt-out (TenantDocument.zbs_enabled,
    // set via ai-receptionist-bot/frontend's ZaloZbsSection toggle) -- skip the
    // oa-status round trip entirely and hide the card. `undefined` (not yet loaded /
    // legacy tenant) is treated as enabled, matching the backend field's default.
    if (zbsEnabled === false) {
      if (!isStale()) {
        setAvailable(false)
        setStatus(null)
        setOaInfo(null)
        setChecked(true)
      }
      return { available: false, status: null, oaInfo: null }
    }
    if (!tenantId || !botId) {
      if (!isStale()) {
        setAvailable(false)
        setStatus(null)
        setOaInfo(null)
        setChecked(true)
      }
      return { available: false, status: null, oaInfo: null }
    }
    try {
      const data = await getEtelecomZbsOaStatus(tenantId, botId)
      const nextStatus = data?.oa_status === 'connected' ? 'connected' : 'not_connected'
      const nextOaInfo = data?.oa_info ?? null
      if (!isStale()) {
        setAvailable(true)
        setStatus(nextStatus)
        setOaInfo(nextOaInfo)
        setChecked(true)
      }
      return { available: true, status: nextStatus, oaInfo: nextOaInfo }
    } catch {
      // Any non-2xx (400, etc.) => no eTelecom shop for this tenant yet.
      // Hide the whole card, fail closed.
      if (!isStale()) {
        setAvailable(false)
        setStatus(null)
        setOaInfo(null)
        setChecked(true)
      }
      return { available: false, status: null, oaInfo: null }
    }
  }, [tenantId, botId, zbsEnabled])

  useEffect(() => {
    setChecked(false)
    setOpen(false)
    setConnecting(false)
    setError('')
    setAuthUrl('')
    setPendingOpen(false)
    loadStatus()
  }, [loadStatus])

  async function handlePopupClosed() {
    // The popup itself doesn't signal success/failure — re-fetch status to
    // learn the real outcome.
    const result = await loadStatus()
    setConnecting(false)
    if (result.status === 'connected') {
      setError('')
    } else {
      setError(t('chatIntegrations.zbsAuthFailed', {
        defaultValue: 'Xác thực chưa hoàn tất, vui lòng thử lại.',
      }))
    }
  }

  const openPopup = useOAuthPopup(authUrl, handlePopupClosed)

  // authUrl is only known after the async connect POST resolves, so the
  // popup can't be opened synchronously from the click handler the way
  // ZaloConnectButton/FacebookConnectButton do with their static URLs.
  // Trigger it reactively once the URL lands.
  useEffect(() => {
    if (pendingOpen && authUrl) {
      setPendingOpen(false)
      openPopup()
    }
  }, [pendingOpen, authUrl, openPopup])

  async function handleConnectClick(e) {
    e.stopPropagation()
    if (connecting) return
    setConnecting(true)
    setError('')
    try {
      const data = await connectEtelecomZbsOa(botId)
      const url = data?.url
      if (!url) throw new Error('missing authorize url')
      setAuthUrl(url)
      setPendingOpen(true)
    } catch (err) {
      setConnecting(false)
      setError(formatApiError(err, t('chatIntegrations.zbsAuthFailed', {
        defaultValue: 'Xác thực chưa hoàn tất, vui lòng thử lại.',
      })))
    }
  }

  // Unconditional, render-time guard -- independent of `checked`/`available`
  // (which depend on async fetch timing/ordering). Even if a stale in-flight
  // request somehow slipped through, this still blocks the render every pass.
  if (zbsEnabled === false) return null
  if (!checked || !available) return null

  const connected = status === 'connected'

  return (
    <div style={cardStyle}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0, marginRight: 'auto' }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>
            {t('chatIntegrations.zbsTitle', { defaultValue: 'Zalo OA (ZBS)' })}
          </p>
          {connected
            ? <span style={badgeConnected}>{t('chatIntegrations.connected')}</span>
            : <span style={badgeNotConnected}>{t('chatIntegrations.zbsNotConnected', { defaultValue: 'Chưa kết nối' })}</span>
          }
        </div>
        <span style={{ fontSize: 18, color: '#888', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>⌄</span>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          {connected ? (
            <div>
              <label style={labelStyle}>{t('chatIntegrations.zbsOaId', { defaultValue: 'OA ID' })}</label>
              <input readOnly style={inputStyle} type="text" value={oaInfo?.oa_id ?? ''} />
              <TemplateSection tenantId={tenantId} botId={botId} t={t} />
              <SendZnsSection tenantId={tenantId} botId={botId} t={t} />
            </div>
          ) : (
            <div>
              <button type="button" style={btnPrimary} disabled={connecting} onClick={handleConnectClick}>
                {connecting
                  ? t('chatIntegrations.zbsConnecting', { defaultValue: 'Đang kết nối...' })
                  : t('chatIntegrations.zbsConnectButton', { defaultValue: 'Kết nối OA (ZBS)' })
                }
              </button>
              <p style={{ fontSize: 11, color: '#888', marginTop: 6, textAlign: 'center' }}>
                {t('chatIntegrations.zbsConnectHelp', { defaultValue: 'Bạn sẽ được chuyển đến trang xác thực Zalo OA (ZBS)' })}
              </p>
              {error && (
                <p style={{ fontSize: 12, color: '#ef4444', marginTop: 8, textAlign: 'center' }}>{error}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
